// @intent: Renderer-neutral compiled view of a Project scene_graph for runtime adapters and verifiers
// @flow: Project -> compileProjectRenderModel -> Pixi adapter renders RenderNode tree without Layer traversal logic
// @stale: update when ProjectBodyV1 layer variants or TZ runtime verifier needs change

import type { ButtonLayer, GroupLayer, Layer, Project, RectLayer, SceneLayer, SpriteLayer, TextLayer } from '../types';
import { countLayers } from './scene-utils';

export type RenderNode =
  | RenderContainerNode
  | RenderRectNode
  | RenderSpriteNode
  | RenderTextNode
  | RenderButtonNode;

export interface RenderNodeBase {
  readonly id: string;
  readonly name: string;
  readonly type: 'container' | 'rect' | 'sprite' | 'text' | 'button';
  readonly layerType: Layer['type'];
  readonly visible: boolean;
  readonly x: number;
  readonly y: number;
  readonly worldX: number;
  readonly worldY: number;
}

export interface RenderContainerNode extends Omit<RenderNodeBase, 'type' | 'layerType'> {
  readonly type: 'container';
  readonly layerType: SceneLayer['type'] | GroupLayer['type'];
  readonly children: readonly RenderNode[];
}

export interface RenderRectNode extends Omit<RenderNodeBase, 'type' | 'layerType'> {
  readonly type: 'rect';
  readonly layerType: RectLayer['type'];
  readonly width: number;
  readonly height: number;
  readonly color: string;
  readonly assetUrl: string | undefined;
  readonly alpha: number;
}

export interface RenderSpriteNode extends Omit<RenderNodeBase, 'type' | 'layerType'> {
  readonly type: 'sprite';
  readonly layerType: SpriteLayer['type'];
  readonly width: number;
  readonly height: number;
  readonly assetUrl: string;
  readonly tint: string | undefined;
  readonly alpha: number;
}

export interface RenderTextNode extends Omit<RenderNodeBase, 'type' | 'layerType'> {
  readonly type: 'text';
  readonly layerType: TextLayer['type'];
  readonly text: string;
  readonly fontSize: number;
  readonly color: string;
  readonly fontFamily: string;
}

export interface RenderButtonNode extends Omit<RenderNodeBase, 'type' | 'layerType'> {
  readonly type: 'button';
  readonly layerType: ButtonLayer['type'];
  readonly width: number;
  readonly height: number;
  readonly label: string;
  readonly color: string;
  readonly assetUrl: string | undefined;
}

export interface RenderSlotRef {
  readonly slotId: string;
  readonly layerId: string;
  readonly label: string;
  readonly slotKind: string;
}

export interface RenderNodeBounds {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface RenderModelError {
  readonly code: 'unknown-layer-type' | 'slot-target-missing' | 'root-not-container';
  readonly layerId?: string;
  readonly slotId?: string;
  readonly message: string;
}

export interface RenderModel {
  readonly projectId: string;
  readonly viewport: { readonly width: number; readonly height: number };
  readonly root: RenderContainerNode;
  readonly layerCount: number;
  readonly assetUrls: readonly string[];
  readonly slots: readonly RenderSlotRef[];
  readonly errors: readonly RenderModelError[];
}

function renderBase(layer: Layer, worldX: number, worldY: number): Omit<RenderNodeBase, 'type' | 'layerType'> {
  return {
    id: layer.id,
    name: layer.name,
    visible: layer.visible,
    x: layer.x,
    y: layer.y,
    worldX,
    worldY,
  };
}

function compileLayer(
  layer: Layer,
  parentWorldX: number,
  parentWorldY: number,
  errors: RenderModelError[],
  slotAssetOverrides: ReadonlyMap<string, string>,
): RenderNode | null {
  const worldX = parentWorldX + layer.x;
  const worldY = parentWorldY + layer.y;
  const base = renderBase(layer, worldX, worldY);
  const slotAssetUrl = slotAssetOverrides.get(layer.id);

  switch (layer.type) {
    case 'scene':
    case 'group':
      return {
        ...base,
        type: 'container',
        layerType: layer.type,
        children: layer.children
          .map((child) => compileLayer(child, worldX, worldY, errors, slotAssetOverrides))
          .filter((node): node is RenderNode => node !== null),
      };
    case 'rect':
      return {
        ...base,
        type: 'rect',
        layerType: 'rect',
        width: layer.width,
        height: layer.height,
        color: layer.color,
        assetUrl: slotAssetUrl ?? layer.assetUrl,
        alpha: layer.alpha ?? 1,
      };
    case 'sprite':
      return {
        ...base,
        type: 'sprite',
        layerType: 'sprite',
        width: layer.width,
        height: layer.height,
        assetUrl: slotAssetUrl ?? layer.assetUrl,
        tint: layer.tint,
        alpha: layer.alpha ?? 1,
      };
    case 'text':
      return {
        ...base,
        type: 'text',
        layerType: 'text',
        text: layer.text,
        fontSize: layer.fontSize,
        color: layer.color,
        fontFamily: layer.fontFamily ?? 'Inter',
      };
    case 'button':
      return {
        ...base,
        type: 'button',
        layerType: 'button',
        width: layer.width,
        height: layer.height,
        label: layer.label,
        color: layer.color,
        assetUrl: slotAssetUrl ?? layer.assetUrl,
      };
    default: {
      const unknownLayer = layer as { readonly id?: string; readonly type?: string };
      errors.push({
        code: 'unknown-layer-type',
        ...(unknownLayer.id ? { layerId: unknownLayer.id } : {}),
        message: `unknown layer type ${unknownLayer.type ?? 'unknown'}`,
      });
      return null;
    }
  }
}

function invalidRootFallback(project: Project, compiledRoot: RenderNode | null, errors: RenderModelError[]): RenderContainerNode {
  const root = project.scene_graph.root;
  errors.push({
    code: 'root-not-container',
    layerId: root.id,
    message: `scene root ${root.id} compiled to ${compiledRoot?.type ?? 'null'} instead of container`,
  });

  return {
    id: root.id,
    name: root.name,
    visible: root.visible,
    x: root.x,
    y: root.y,
    worldX: root.x,
    worldY: root.y,
    type: 'container',
    layerType: 'scene',
    children: [],
  };
}

function collectNodeAssetUrls(node: RenderNode): string[] {
  if (node.type === 'rect' && node.assetUrl) {
    return [node.assetUrl];
  }

  if (node.type === 'sprite') {
    return [node.assetUrl];
  }

  if (node.type === 'button' && node.assetUrl) {
    return [node.assetUrl];
  }

  if (node.type === 'container') {
    return node.children.flatMap((child) => collectNodeAssetUrls(child));
  }

  return [];
}

export function findRenderNode(root: RenderNode, id: string): RenderNode | null {
  if (root.id === id) return root;
  if (root.type !== 'container') return null;

  for (const child of root.children) {
    const found = findRenderNode(child, id);
    if (found) return found;
  }

  return null;
}

export function collectRenderAssetUrls(model: RenderModel): string[] {
  return [...model.assetUrls];
}

export function resolveRenderNodeBounds(root: RenderNode, id: string): RenderNodeBounds | null {
  const node = findRenderNode(root, id);
  if (!node) return null;

  switch (node.type) {
    case 'rect':
    case 'sprite':
    case 'button':
      return {
        id: node.id,
        x: node.worldX,
        y: node.worldY,
        width: node.width,
        height: node.height,
      };
    default:
      return null;
  }
}

export function compileProjectRenderModel(project: Project): RenderModel {
  const errors: RenderModelError[] = [];
  const slotAssetOverrides = new Map<string, string>();
  for (const slot of Object.values(project.slot_map.slots)) {
    if (typeof slot.assetUrl === 'string' && slot.assetUrl.length > 0) {
      slotAssetOverrides.set(slot.layerId, slot.assetUrl);
    }
  }
  const compiledRoot = compileLayer(project.scene_graph.root, 0, 0, errors, slotAssetOverrides);
  const root = compiledRoot?.type === 'container' ? compiledRoot : invalidRootFallback(project, compiledRoot, errors);
  const assetUrls = [...new Set(collectNodeAssetUrls(root))];
  const slots: RenderSlotRef[] = [];

  for (const [slotId, slot] of Object.entries(project.slot_map.slots)) {
    if (findRenderNode(root, slot.layerId)) {
      slots.push({ slotId, layerId: slot.layerId, label: slot.label, slotKind: slot.kind });
    } else {
      errors.push({
        code: 'slot-target-missing',
        slotId,
        layerId: slot.layerId,
        message: `slot ${slotId} points to missing layer ${slot.layerId}`,
      });
    }
  }

  return {
    projectId: project.id,
    viewport: project.manifest.viewport,
    root,
    layerCount: countLayers(project.scene_graph.root),
    assetUrls,
    slots,
    errors,
  };
}
